/**
 * Stores section: Leaflet map with pins + region filter chips.
 * Region is derived from Brazilian state (UF or full name).
 */

const REGION_ORDER = ['centro-oeste', 'nordeste', 'norte', 'sudeste', 'sul', 'outras'];

const STATE_TO_REGION = {
  ac: 'norte',
  acre: 'norte',
  al: 'nordeste',
  alagoas: 'nordeste',
  ap: 'norte',
  amapa: 'norte',
  amapá: 'norte',
  am: 'norte',
  amazonas: 'norte',
  ba: 'nordeste',
  bahia: 'nordeste',
  ce: 'nordeste',
  ceara: 'nordeste',
  ceará: 'nordeste',
  df: 'centro-oeste',
  'distrito federal': 'centro-oeste',
  es: 'sudeste',
  'espirito santo': 'sudeste',
  'espírito santo': 'sudeste',
  go: 'centro-oeste',
  goias: 'centro-oeste',
  goiás: 'centro-oeste',
  ma: 'nordeste',
  maranhao: 'nordeste',
  maranhão: 'nordeste',
  mt: 'centro-oeste',
  'mato grosso': 'centro-oeste',
  ms: 'centro-oeste',
  'mato grosso do sul': 'centro-oeste',
  mg: 'sudeste',
  'minas gerais': 'sudeste',
  pa: 'norte',
  para: 'norte',
  pará: 'norte',
  pb: 'nordeste',
  paraiba: 'nordeste',
  paraíba: 'nordeste',
  pr: 'sul',
  parana: 'sul',
  paraná: 'sul',
  pe: 'nordeste',
  pernambuco: 'nordeste',
  pi: 'nordeste',
  piaui: 'nordeste',
  piauí: 'nordeste',
  rj: 'sudeste',
  'rio de janeiro': 'sudeste',
  rn: 'nordeste',
  'rio grande do norte': 'nordeste',
  rs: 'sul',
  'rio grande do sul': 'sul',
  ro: 'norte',
  rondonia: 'norte',
  rondônia: 'norte',
  rr: 'norte',
  roraima: 'norte',
  sc: 'sul',
  'santa catarina': 'sul',
  sp: 'sudeste',
  'sao paulo': 'sudeste',
  'são paulo': 'sudeste',
  se: 'nordeste',
  sergipe: 'nordeste',
  to: 'norte',
  tocantins: 'norte',
};

function normalizeKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function getRegion(store) {
  const country = normalizeKey(store.country);
  const isBrazil =
    !country ||
    country === 'br' ||
    country === 'brasil' ||
    country === 'brazil';

  if (!isBrazil) return 'outras';

  const stateKey = normalizeKey(store.state);
  return STATE_TO_REGION[stateKey] || 'outras';
}

function parseStoresJson(section) {
  const el = section.querySelector('[data-stores-json]');
  if (!el) return [];
  try {
    const data = JSON.parse(el.textContent.trim());
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/**
 * Extract lat/lng from Google Maps URLs (embed pb, @lat,lng, q=lat,lng).
 * Short links (maps.app.goo.gl) cannot be parsed without a network redirect.
 */
function parseCoordsFromMapUrl(url) {
  if (!url || typeof url !== 'string') return null;

  const srcMatch = url.match(/src=["']([^"']+)["']/i);
  let raw = (srcMatch ? srcMatch[1] : url).trim();
  try {
    raw = decodeURIComponent(raw);
  } catch {
    // keep raw
  }

  // Embed / pb: !2d{lng}!3d{lat}
  const pb = raw.match(/!2d(-?\d+(?:\.\d+)?)!3d(-?\d+(?:\.\d+)?)/);
  if (pb) {
    const lng = Number(pb[1]);
    const lat = Number(pb[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  // Place URL: @lat,lng,zoom
  const at = raw.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (at) {
    const lat = Number(at[1]);
    const lng = Number(at[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  // Search: ?q=lat,lng or query=lat,lng
  const q = raw.match(/[?&](?:q|query)=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (q) {
    const lat = Number(q[1]);
    const lng = Number(q[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  return null;
}

function isValidCoords(lat, lng) {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  );
}

function resolveStoreCoords(store) {
  const lat = store.lat != null && store.lat !== '' ? Number(store.lat) : null;
  const lng = store.lng != null && store.lng !== '' ? Number(store.lng) : null;
  if (isValidCoords(lat, lng)) return { lat, lng };

  const fromUrl = parseCoordsFromMapUrl(store.mapUrl);
  if (fromUrl && isValidCoords(fromUrl.lat, fromUrl.lng)) return fromUrl;

  return { lat: null, lng: null };
}

function resolveStoreMapLink(store) {
  const mapUrl = store.mapUrl || '';
  const isEmbed =
    /maps\/embed/i.test(mapUrl) || /<iframe/i.test(mapUrl) || /!2d-?\d/.test(mapUrl);

  if (isValidCoords(store.lat, store.lng) && (isEmbed || !mapUrl)) {
    return `https://www.google.com/maps?q=${store.lat},${store.lng}`;
  }

  if (mapUrl && !/<iframe/i.test(mapUrl)) return mapUrl;

  if (isValidCoords(store.lat, store.lng)) {
    return `https://www.google.com/maps?q=${store.lat},${store.lng}`;
  }

  return mapUrl;
}

function createPinIcon() {
  const svg = `
    <svg class="stores__pin-svg" width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M16 0C7.716 0 1 6.716 1 15c0 10.5 12.25 23.75 13.25 24.8a1.2 1.2 0 0 0 1.5 0C16.75 38.75 29 25.5 29 15 29 6.716 22.284 0 16 0Z" fill="#DF8DCC" stroke="#C46BAF" stroke-width="1.5"/>
      <circle cx="16" cy="15" r="6" fill="#f7f4a2" stroke="#C46BAF" stroke-width="1"/>
    </svg>
  `.trim();

  return L.divIcon({
    className: 'stores__pin',
    html: svg,
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -36],
  });
}

function initStores(section) {
  const stores = parseStoresJson(section).map((store) => {
    const coords = resolveStoreCoords(store);
    const next = {
      ...store,
      region: getRegion(store),
      lat: coords.lat,
      lng: coords.lng,
    };
    next.mapUrl = resolveStoreMapLink(next);
    return next;
  });

  const cards = Array.from(section.querySelectorAll('[data-store-card]'));
  const chipsWrap = section.querySelector('[data-stores-chips]');
  const chipButtons = chipsWrap
    ? Array.from(chipsWrap.querySelectorAll('[data-region]'))
    : [];
  const emptyFilter = section.querySelector('[data-stores-empty-filter]');
  const mapEl = section.querySelector('[data-stores-map]');

  // Keep card "Veja a loja" links usable when map_url is an embed URL
  cards.forEach((card) => {
    const id = Number(card.getAttribute('data-store-id'));
    const store = stores.find((s) => s.id === id);
    if (!store || !store.mapUrl) return;
    const link = card.querySelector('.stores__card-btn');
    if (link) link.setAttribute('href', store.mapUrl);
  });

  const presentRegions = new Set(stores.map((s) => s.region));
  chipButtons.forEach((btn) => {
    const region = btn.getAttribute('data-region') || '';
    if (region === '') {
      btn.hidden = false;
      return;
    }
    btn.hidden = !presentRegions.has(region);
  });

  // Reorder visible region chips (keep "all" first)
  if (chipsWrap) {
    const allChip = chipButtons.find((b) => b.getAttribute('data-region') === '');
    REGION_ORDER.forEach((region) => {
      const btn = chipButtons.find((b) => b.getAttribute('data-region') === region);
      if (btn && !btn.hidden) chipsWrap.appendChild(btn);
    });
    if (allChip) chipsWrap.insertBefore(allChip, chipsWrap.firstChild);
  }

  let map = null;
  let markersLayer = null;
  const markersById = new Map();
  let activeRegion = '';
  let activeStoreId = null;

  function storesWithCoords(list) {
    return list.filter((s) => isValidCoords(s.lat, s.lng));
  }

  function initMap() {
    if (!mapEl || typeof L === 'undefined') return;

    const withCoords = storesWithCoords(stores);
    if (!withCoords.length) {
      mapEl.hidden = true;
      return;
    }

    map = L.map(mapEl, {
      scrollWheelZoom: false,
      attributionControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    markersLayer = L.layerGroup().addTo(map);
    const icon = createPinIcon();

    withCoords.forEach((store) => {
      const marker = L.marker([store.lat, store.lng], { icon });
      if (store.name) marker.bindPopup(store.name);
      marker.on('click', () => {
        focusStore(store.id, { fromMap: true });
      });
      markersById.set(store.id, marker);
      markersLayer.addLayer(marker);
    });

    fitMapToVisible();
    // Leaflet needs a resize after layout
    requestAnimationFrame(() => {
      map.invalidateSize();
      fitMapToVisible();
    });
  }

  function fitMapToVisible() {
    if (!map || !markersLayer) return;
    const layers = [];
    markersLayer.eachLayer((layer) => layers.push(layer));
    if (!layers.length) return;
    if (layers.length === 1) {
      map.setView(layers[0].getLatLng(), 12);
      return;
    }
    const group = L.featureGroup(layers);
    map.fitBounds(group.getBounds().pad(0.2));
  }

  function applyFilter(region) {
    activeRegion = region || '';
    let visibleCount = 0;

    stores.forEach((store) => {
      const visible = !activeRegion || store.region === activeRegion;
      const card = cards.find((c) => Number(c.getAttribute('data-store-id')) === store.id);
      if (card) {
        card.hidden = !visible;
        card.classList.toggle('is-active', visible && activeStoreId === store.id);
      }

      const marker = markersById.get(store.id);
      if (marker && markersLayer) {
        if (visible) {
          if (!markersLayer.hasLayer(marker)) markersLayer.addLayer(marker);
        } else if (markersLayer.hasLayer(marker)) {
          markersLayer.removeLayer(marker);
        }
      }

      if (visible) visibleCount += 1;
    });

    if (emptyFilter) emptyFilter.hidden = visibleCount > 0;
    fitMapToVisible();
  }

  function setActiveChip(region) {
    chipButtons.forEach((btn) => {
      const isActive = (btn.getAttribute('data-region') || '') === region;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function focusStore(id, { fromMap } = {}) {
    activeStoreId = id;
    const store = stores.find((s) => s.id === id);
    if (!store) return;

    if (activeRegion && store.region !== activeRegion) {
      setActiveChip('');
      applyFilter('');
    }

    cards.forEach((card) => {
      const match = Number(card.getAttribute('data-store-id')) === id;
      card.classList.toggle('is-active', match);
      if (match && fromMap) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });

    const marker = markersById.get(id);
    if (marker && map) {
      map.panTo(marker.getLatLng(), { animate: true });
      marker.openPopup();
    }
  }

  chipButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const region = btn.getAttribute('data-region') || '';
      setActiveChip(region);
      applyFilter(region);
    });
  });

  cards.forEach((card) => {
    card.addEventListener('click', (event) => {
      if (event.target.closest('a')) return;
      const id = Number(card.getAttribute('data-store-id'));
      focusStore(id, { fromMap: false });
    });
  });

  if (document.readyState === 'complete') {
    initMap();
  } else {
    window.addEventListener('load', initMap, { once: true });
  }

  // Leaflet may load after this script (both defer) — retry briefly
  if (typeof L === 'undefined') {
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (typeof L !== 'undefined') {
        clearInterval(timer);
        initMap();
      } else if (attempts > 40) {
        clearInterval(timer);
      }
    }, 50);
  }
}

function initStoresSections() {
  document.querySelectorAll('[data-stores]').forEach((section) => {
    if (section.dataset.storesReady === 'true') return;
    if (!section.querySelector('[data-store-card]')) return;
    section.dataset.storesReady = 'true';
    initStores(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStoresSections);
} else {
  initStoresSections();
}
